/**
 * Non-streaming response handler — JSON parsing, mimick typing, rich media extraction.
 */
import { devLog } from './devLog';
import { extractAIResponse, isToolCallResponse, extractRichMedia, buildAIMessage } from './responseParser';

/**
 * Process a non-streaming JSON response from the AI API.
 *
 * @param {Object} data - parsed JSON response
 * @param {string} selectedModel - current model ID
 * @param {Object} opts
 * @param {Function} opts.setMessages
 * @param {Function} opts.setIsWebSearching
 * @param {Function} opts.setQnaData
 * @param {Function} opts.setIsMimicking
 * @param {boolean} opts.enable_mimick
 * @param {boolean} opts.stopRequested
 * @param {Object} opts.intervalIdRef
 * @param {Object} opts.memoryManagerRef
 * @param {Object} opts.screenshotInProgress
 * @param {Function} opts.handleScreenshotRequest
 * @param {string} opts.userMessageText - original user input text
 * @param {string} opts.currentSessionId
 * @param {Function} opts.setSessions
 * @param {Function} opts.refreshSessions
 * @returns {{ handled: boolean }} - if screenshot was requested, handled=true means caller should return
 */
export const processNonStreamingResponse = (data, selectedModel, opts) => {
  const {
    setMessages, setIsWebSearching, setQnaData, setIsMimicking,
    enable_mimick, stopRequested, intervalIdRef, memoryManagerRef,
    screenshotInProgress, handleScreenshotRequest, userMessageText,
    currentSessionId, setSessions, refreshSessions,
  } = opts;

  devLog(`📥 Non-streaming response - parsing JSON...`);
  devLog(`📋 Raw response data:`, data);

  // Handle screenshot request (client-side tool)
  if (data.screenshot_requested) {
    devLog(`📸 AI requested screenshot: ${data.reason}`);
    screenshotInProgress.current = true;
    handleScreenshotRequest(data.reason, userMessageText);
    return { handled: true };
  }

  // Update session title if backend auto-generated one
  if (data.sessionTitle && currentSessionId) {
    devLog(`🏷️ Auto-generated session title: ${data.sessionTitle}`);
    setSessions(prev => prev.map(s =>
      s.session_id === currentSessionId ? { ...s, title: data.sessionTitle } : s
    ));
    refreshSessions();
  }

  // Parse response using model-specific format
  const aiRawResponse = extractAIResponse(data, selectedModel);

  // Detect tool call pattern — backend handles it, don't display
  if (isToolCallResponse(aiRawResponse)) {
    devLog('🔍 Tool call pattern detected - web search in progress');
    setIsWebSearching(true);
    return { handled: true };
  }

  // Extract rich media and tool results
  const richMedia = extractRichMedia(data);
  if (richMedia.userInputRequest) {
    setQnaData({ questions: richMedia.userInputRequest });
  }

  // Build final AI message
  const aiMessage = buildAIMessage(aiRawResponse, selectedModel, richMedia);

  if (memoryManagerRef.current && typeof memoryManagerRef.current.addAIMessage === 'function') {
    memoryManagerRef.current.addAIMessage(aiMessage.text);
  }

  // Mimick typing effect (DeepSeek only)
  if (selectedModel === "deepseek-r1:32b" && enable_mimick) {
    setMessages((prev) => [...prev, { role: "ai", text: "" }]);
    setIsMimicking(true);
    let index = 0;
    let displayedText = "";

    const newInterval = setInterval(() => {
      if (stopRequested) {
        clearInterval(newInterval);
        setIsMimicking(false);
        return;
      }

      if (index < aiMessage.text.length) {
        displayedText += aiMessage.text[index];
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: "ai", text: displayedText };
          return updated;
        });
        index += 1;
      } else {
        clearInterval(newInterval);
        setIsMimicking(false);
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: "ai", text: aiMessage.text };
          return updated;
        });
      }
    }, Math.random() * (5 - 1) + 1);

    intervalIdRef.current = newInterval;
  } else {
    // Standard: add empty then replace with full message
    setMessages((prev) => [...prev, { role: "ai", text: "" }]);
    setMessages((prev) => {
      const updated = [...prev.slice(0, -1), aiMessage];
      return updated;
    });
  }

  return { handled: false };
};
